import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useGlobalState } from '../../app/GlobalStateProvider';
import { Button } from '../../base/common';
import { RemaingTimer } from '../../base/question/RemaingTimer';
import { AudioPlayer, clearMockAudio } from '../../base/question/AudioPlayer';
import { pauseTimer } from '../../base/question/querys';
import { Icon } from '../../base/ui';
import { getIsTeacher } from '../../base/local';
import { onEnterTestRoom } from '../action';
import { getFocusTestId, getIsEnterTestRoom, getIsMockMode, getIsPersonalTimerStarted, getIsSubmitUser, getIsTestFoldModal, getIsTestOpenModal, getTestBackgroundUrl, getTestItemByTestId, getTestList } from '../functions';
// @ts-ignore
const TestModeStyled = styled.div `
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	position: relative;
	${props => props.$isIpad && 'touch-action: none;'}

	.device-container {
		display: flex;
		flex-direction: column;
		.device_wrapper {
			width: 100%;
			height: 100%;
			flex: 1;
		}
		.overText2 {
			white-space: break-spaces;
		}

		.device_title_container {
			width: 100%;
			height: 100%;
			${props => props.$isIpad && 'touch-action: none;'}

			.device_container {
				width: ${props => (!props.$isDesktop ? '100%' : '402px')};
				overflow: auto;
				${props => props.$isIpad && 'touch-action: none;'}
			}
			.device_guid {
				width: ${props => (!props.$isDesktop ? '100%' : '402px')};
				overflow: hidden;

				font-size: ${props => (!props.$isDesktop ? '20px' : '24px;')};
				white-space: break-spaces;
			}
		}
	}

	.device_btn {
		p {
			color: #fff;
			text-align: left;
			font-family: Pretendard;
			font-size: ${props => (!props.$isDesktop ? '16px' : '24px;')};
			font-style: normal;
			font-weight: 700;
			letter-spacing: -0.4px;
			line-height: 40px;
		}
	}

	.red_time {
		position: absolute;
		bottom: 20px;
		left: 50%;
		transform: translate(-50%, 0);
		.text-field {
			font-size: 60px;
			text-align: center;

			padding: 20px;
			box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.5);
			box-sizing: border-box;
			background-color: red;
			border-radius: 20px;

			color: #fff;
		}

		/* 종료 상태 박스 — 사유별 색 전환(시간초과=회색, 자진제출=초록) + 타이틀·보조·캡션 */
		.timer-ended {
			min-width: 240px;
			max-width: 360px;
			padding: 20px 28px;
			box-sizing: border-box;
			border-radius: 20px;
			box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.5);
			text-align: center;
			color: #fff;
		}
		.timer-ended.is-auto {
			background-color: #5b5b5b;
		}
		.timer-ended.is-manual {
			background-color: #4a7a3f;
		}
		.timer-ended-title {
			font-size: 32px;
			font-weight: 700;
			line-height: 1.3;
		}
		.timer-ended-sub {
			font-size: 20px;
			opacity: 0.85;
			margin-top: 4px;
		}
		.timer-ended-caption {
			font-size: 15px;
			margin-top: 12px;
			padding-top: 12px;
			border-top: 1px solid rgba(255, 255, 255, 0.25);
			opacity: 0.9;
			line-height: 1.4;
			white-space: break-spaces;
		}
	}

	.test-mode-head {
		display: flex;
		padding: 20px;
		box-sizing: border-box;
		width: 100%;
		background-color: #000;
	}

	img {
		width: 100%;
		height: 100%;
		object-fit: contain;
		overflow: hidden;
	}
`;
// 분 단위 진행시간(테스트 풀이시간)을 red_time 표시용 HH:MM:SS 문자열로 변환
const formatDurationHHMMSS = (minutes) => {
    const total = Math.max(0, Math.floor((minutes || 0) * 60));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return [h, m, s].map(v => v.toString().padStart(2, '0')).join(':');
};
/**
 * 모의고사 모드인 경우 입장 화면 및 모의고사 진행 시간을 보여주는 컴포넌트
 */
export const OnlyTestMode = React.memo(() => {
    const { isDesktop } = useGlobalState();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isEnterTestRoom = useSelector((state) => getIsEnterTestRoom(state));
    const testItem = useSelector((state) => getTestItemByTestId(state));
    const backgroundUrl = useSelector((state) => getTestBackgroundUrl(state));
    const isTeacher = useSelector((state) => getIsTeacher(state));
    const focusTestId = useSelector((state) => getFocusTestId(state));
    const testList = useSelector((state) => getTestList(state), shallowEqual);
    const openModal = useSelector((state) => getIsTestOpenModal(state));
    const foldModal = useSelector((state) => getIsTestFoldModal(state));
    const onEnterTest = () => {
        dispatch(onEnterTestRoom());
    };
    const isIpad = /iPad/.test(window.navigator.userAgent) ||
        (window.navigator.userAgent.includes('Macintosh') && 'ontouchend' in document);
    // 개인 타이머 표식: 학생이 실제 문항/첨부파일을 확인해 개별 타이머가 시작된 테스트만 red_time 카운트다운
    // (서버 remaining은 전체 타이머 기준이라 신뢰 불가). 시작 시점은 redux(personalStartedTimers)로 관리해
    // 풀이화면에서 타이머가 시작되면 본 컴포넌트도 함께 재렌더되도록 한다. (영속은 sessionStorage가 담당)
    const activeTestId = focusTestId || testItem?.id;
    const personalStarted = useSelector((state) => getIsPersonalTimerStarted(state, activeTestId));
    // 제출 완료 여부 — 제출 완료 시 red_time 카운팅을 멈추고 '00:00:00' 표시(모드 무관).
    // (notSubmit: true=미제출, false=제출완료, 'loading'=조회중 → false일 때만 정지)
    const isMockMode = useSelector((state) => getIsMockMode(state));
    const notSubmit = useSelector((state) => getIsSubmitUser(state));
    const submitted = notSubmit === false;
    // 듣기 mp3: 첨부에 .mp3가 있으면 그 URL (오디오 플레이어용)
    const mp3Url = useMemo(() => {
        const list = testItem?.attachments || [];
        const found = list.find((a) => typeof a === 'string' && a.toLowerCase().endsWith('.mp3'));
        return typeof found === 'string' ? found : '';
    }, [testItem?.attachments]);
    // 테스트 종료(상태 비-open) 또는 모의고사 강의실 퇴장 시 듣기 오디오 싱글톤 정리.
    // (모달 열림/접힘/닫힘은 isEnterTestRoom·status를 바꾸지 않으므로 위치 전환 시엔 정리되지 않아 무중단 유지)
    useEffect(() => {
        if (!isEnterTestRoom || (testItem && testItem.status !== 'open')) {
            clearMockAudio();
        }
    }, [isEnterTestRoom, testItem?.status]);
    // 모의고사 강의실 퇴장(탭 닫기/이동) 시: 시작한(표식 있는) open 테스트의 개별 타이머를 일시정지(pause API, body 없음).
    // pagehide/beforeunload + keepalive 로 언로드 중에도 전송되도록 처리.
    useEffect(() => {
        if (isTeacher)
            return;
        const pauseStartedTimers = () => {
            (testList || []).forEach((tt) => {
                if (tt.status !== 'open')
                    return;
                let started = false;
                try {
                    started = sessionStorage.getItem(`mock_personal_timer:${tt.id}`) === '1';
                }
                catch (e) { }
                if (started) {
                    pauseTimer(tt.id).catch(() => { });
                }
            });
        };
        window.addEventListener('pagehide', pauseStartedTimers);
        window.addEventListener('beforeunload', pauseStartedTimers);
        return () => {
            window.removeEventListener('pagehide', pauseStartedTimers);
            window.removeEventListener('beforeunload', pauseStartedTimers);
        };
    }, [isTeacher, testList]);
    return (React.createElement(TestModeStyled, { className: "test_layout", "$isDesktop": isDesktop, "$isIpad": isIpad }, isEnterTestRoom ? (React.createElement(React.Fragment, null,
        !isTeacher &&
            personalStarted &&
            testItem?.status === 'open' &&
            mp3Url &&
            !(openModal && !foldModal && focusTestId) && (React.createElement("div", { style: {
                position: 'fixed',
                top: 24,
                left: 24,
                zIndex: 99999,
                width: 'min(820px, calc(100vw - 48px))',
                boxSizing: 'border-box',
            } },
            React.createElement(AudioPlayer, { src: mp3Url, testId: testItem.id, duration: testItem.duration }))),
        !isTeacher && (React.createElement("div", { className: "red_time" }, personalStarted && testItem ? (React.createElement(RemaingTimer, { test_id: testItem.id, stopped: submitted, variant: "box" })) : (React.createElement("div", { className: "text-field", style: { margin: 0 } }, formatDurationHHMMSS(testItem?.duration))))),
        React.createElement("img", { src: backgroundUrl, alt: "mock_test background" }))) : (React.createElement("div", { className: "device-container" },
        React.createElement("div", { className: "device_wrapper" },
            React.createElement("div", { className: "device_title_container" },
                React.createElement("div", { className: "device_container" },
                    React.createElement(React.Fragment, null,
                        React.createElement(Icon, { icon: "device_connect", size: isDesktop ? 68 : 48, color: "#B9D1EF" }),
                        React.createElement("p", { className: "device_title" }, t('deviceSelection.testMode')),
                        React.createElement("p", { className: "device_guid" }, t('deviceSelection.testModeGuid'))),
                    React.createElement(Button, { className: "device_btn", onClick: onEnterTest, text: 'deviceSelection.enterTestMode', option: {
                            buttonBeforeIcon: {
                                show: true,
                                name: 'nickname_enter',
                                size: 24,
                                color: '#FFF',
                            },
                        } }))))))));
});
